import { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore';
import { useLogout } from '../features/auth/useAuth';
import { NotificationCenter } from '../features/notifications/components/NotificationCenter';
import { useTranslation } from 'react-i18next';
import { useRealtime } from '../hooks/useRealtime';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Sidebar } from '../components/Sidebar';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Globe,
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Bot,
  Sun,
  Moon,
  Building2
} from 'lucide-react';
import { authApi } from '../features/auth/authApi';
import { GlobalCopilot } from '../components/copilot/GlobalCopilot';
import { hasModuleAccess } from '../config/moduleRegistry';
import { useCopilotStore } from '../stores/copilotStore';
import { useTheme } from '../components/theme/ThemeProvider';
import { WhatsAppHeartbeatIndicator } from '../components/WhatsAppHeartbeatIndicator';
import { ReflectionGlobalIndicator } from '../features/knowledge/ReflectionGlobalIndicator';

export function AppLayout() {
  const { t, i18n } = useTranslation();
  useRealtime();
  const user = useSessionStore((state) => state.user);
  const activeTenantId = useSessionStore((state) => state.activeTenantId);
  const activeTenantName = useSessionStore((state) => state.activeTenantName);
  const setActiveTenant = useSessionStore((state) => state.setActiveTenant);
  const queryClient = useQueryClient();
  const logout = useLogout();
  const { theme, toggleTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('crm-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const activeTenantLogo = useSessionStore((state) => state.activeTenantLogo);
  const setActiveTenantLogo = useSessionStore((state) => state.setActiveTenantLogo);
  const [logoUrl, setLogoUrl] = useState(activeTenantLogo || user?.logo_url || 'https://inteligenciaenegocios.com.br/assets/logo.jpg');
  const [copilotOpen, setCopilotOpen] = useState(false);
  const copilotStore = useCopilotStore();
  const hasCopilot = hasModuleAccess(user, 'ai_agents') || hasModuleAccess(user, 'copilot');

  useEffect(() => {
    if (activeTenantLogo) {
      setLogoUrl(activeTenantLogo);
    }
  }, [activeTenantLogo]);

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        const domain = window.location.hostname;
        const tenantToFetch = activeTenantId || user?.tenant_id;
        const info = await authApi.tenantInfo(domain, tenantToFetch || undefined);
        const tenantName = (info as any)?.name || (user as any)?.tenant_name || user?.tenant_id || 'Inteligência e Negócios Tecnologia';
        document.title = `CRM - ${tenantName}`;
        if (info?.logo_url && info.logo_url !== activeTenantLogo) {
          setLogoUrl(info.logo_url);
          setActiveTenantLogo(info.logo_url);
        }
      } catch (err) {
        document.title = 'CRM - Inteligência e Negócios Tecnologia';
      }
    };
    fetchTenantData();
  }, [activeTenantId, user?.tenant_id, activeTenantLogo, setActiveTenantLogo]);

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try { localStorage.setItem('crm-sidebar-collapsed', next ? '1' : '0'); } catch { /* noop */ }
  };

  const toggleLanguage = () => {
    const next = i18n.language.startsWith('pt') ? 'en' : 'pt-BR';
    i18n.changeLanguage(next);
  };

  return (
    <div className="app-shell">
      {/* Sidebar — hidden on mobile */}
      {!isMobile && <Sidebar collapsed={sidebarCollapsed} />}

      {/* Mobile Drawer */}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-drawer-header">
              <span className="mobile-drawer-title">Menu</span>
              <button className="mobile-drawer-close" onClick={() => setMobileMenuOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div onClick={() => setMobileMenuOpen(false)}>
              <Sidebar collapsed={false} />
            </div>
          </div>
        </div>
      )}

      <div className="app-main">
        {/* Topbar */}
        <header className="topbar">
          <div className="topbar-left" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="topbar-btn" onClick={isMobile ? () => setMobileMenuOpen(true) : toggleSidebar}>
              <Menu size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img 
                src={logoUrl} 
                alt="Logo" 
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://inteligenciaenegocios.com.br/assets/logo.jpg';
                }}
                style={{ height: '40px', width: 'auto', borderRadius: '6px', flexShrink: 0, objectFit: 'contain' }} 
              />
            </div>
            <div className="topbar-divider hidden-mobile" />
            <WhatsAppHeartbeatIndicator />
            {activeTenantId && activeTenantId !== 'vanguarda' && (
              <div 
                className="hidden-mobile"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  background: 'rgba(59, 130, 246, 0.15)',
                  border: '1px solid rgba(59, 130, 246, 0.35)',
                  color: '#60a5fa',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
                title="Você está interagindo com a visão deste tenant ativo"
              >
                <Building2 size={13} />
                <span>Visão: <strong>{activeTenantName || activeTenantId}</strong></span>
                <button 
                  onClick={() => {
                    setActiveTenant(null);
                    queryClient.invalidateQueries();
                    toast.info('Visão da sessão restaurada para o tenant padrão.');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#93c5fd',
                    cursor: 'pointer',
                    padding: '0 2px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Restaurar tenant padrão"
                >
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          <div className="topbar-actions">
            {hasCopilot && (
              <button className="topbar-btn" onClick={() => copilotStore.openGeneral()} title="CRM Copilot">
                <Bot size={16} />
                <span className="hidden-mobile">Copilot</span>
              </button>
            )}

            <button 
              className="topbar-btn" 
              onClick={toggleTheme} 
              title={theme === 'dark' ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
              <span className="hidden-mobile">{theme === 'dark' ? 'Claro' : 'Escuro'}</span>
            </button>

            <button className="topbar-btn" onClick={toggleLanguage}>
              <Globe size={14} />
              <span>{i18n.language.startsWith('pt') ? 'PT' : 'EN'}</span>
            </button>

            <div className="topbar-divider" />

            <NotificationCenter />

            <NavLink to="/profile" className="topbar-btn">
              <User size={14} />
              <span className="hidden-mobile">{user?.name?.split(' ')[0] || 'Perfil'}</span>
            </NavLink>

            <NavLink to="/settings" className="topbar-btn">
              <Settings size={14} />
              <span className="hidden-mobile">{t('sidebar.settings')}</span>
            </NavLink>

            <button className="topbar-btn logout-btn" onClick={() => logout.mutate()} disabled={logout.isPending}>
              <LogOut size={14} />
              <span className="hidden-mobile">Sair</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <section className="content-area">
          <main className="page-content">
            <Outlet />
          </main>
        </section>
      </div>

      {hasCopilot && (
        <GlobalCopilot isOpen={copilotStore.isOpen || copilotOpen} onClose={() => { copilotStore.close(); setCopilotOpen(false); }} />
      )}

      <ReflectionGlobalIndicator />
    </div>
  );
}
